import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { RoadmapContent } from "./RoadmapContent";
import { getAuthHeader } from "../../utils/auth";

const PROD_API_URL = "https://theqahubweb-production.up.railway.app";
const ENV_API_URL = (process.env.REACT_APP_API_URL || "").trim().replace(/\/$/, "");
const CURRENT_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const NORMALIZED_ORIGIN = CURRENT_ORIGIN.includes("://www.theqahub.es")
  ? CURRENT_ORIGIN.replace("://www.", "://")
  : CURRENT_ORIGIN;
const getStoredApiUrl = () => {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem("theqahub_api_base") || "";
  } catch {
    return "";
  }
};
const STORED_API_URL = getStoredApiUrl();
const SHOULD_USE_ORIGIN = CURRENT_ORIGIN.includes("theqahub.es");
const API_URL = STORED_API_URL || (SHOULD_USE_ORIGIN ? NORMALIZED_ORIGIN : (ENV_API_URL || PROD_API_URL));
const noop = () => {};

export default function RoadmapTopic() {
  const { topic } = useParams();
  const navigate = useNavigate();

  const outlet = useOutletContext();
  const progress = outlet?.progress ?? {};
  const setProgress = outlet?.setProgress ?? noop;
  const setHeroTopic = outlet?.setHeroTopic ?? noop;

  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const [slideIndex, setSlideIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // index -> option

  const content = useMemo(() => RoadmapContent[topic], [topic]);
  const slides = useMemo(() => content?.slides || [], [content]);
  const quiz = useMemo(() => content?.quiz || [], [content]);

  const done = !!progress?.[topic]?.completed;

  // Prev/Next correctamente
  const canGoPrev = slideIndex > 0;
  const canGoNext = slideIndex < slides.length - 1;

  // Bloqueo del quiz hasta llegar al último slide
  const hasSlides = slides.length > 0;
  const isLastSlide = !hasSlides ? true : slideIndex === slides.length - 1;
  const quizLocked = hasSlides && !isLastSlide;

  const scorePreview = useMemo(() => {
    if (!quiz.length) return 0;
    let correct = 0;
    quiz.forEach((item, i) => {
      if (answers[i] === item.correct) correct++;
    });
    return Math.round((correct / quiz.length) * 100);
  }, [answers, quiz]);

  const submitQuiz = async () => {
    setError("");
    setMsg("");
    setSending(true);
    try {
      if (!quiz.length) {
        setMsg("Este tema aún no tiene quiz.");
        return;
      }
      // Seguridad extra: no permitir enviar si está bloqueado
      if (quizLocked) {
        setError("Debes terminar todos los slides para desbloquear el quiz.");
        return;
      }

      let correct = 0;
      quiz.forEach((item, i) => {
        if (answers[i] === item.correct) correct++;
      });
      const score = Math.round((correct / quiz.length) * 100);

      const res = await fetch(`${API_URL}/api/auth/roadmap/quiz`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        credentials: "include",
        body: JSON.stringify({ topic, score }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || "Error enviando quiz");
        return;
      }

      setProgress(data.roadmap || {});
      setMsg(
        `Resultado: ${score}%. ${
          score >= 70 ? "Tema completado" : "Sigue practicando"
        }`
      );
    } finally {
      setSending(false);
    }
  };

  const completeManually = async () => {
    setError("");
    setMsg("");
    setSending(true);
    try {
      // Seguridad extra: no permitir completar si está bloqueado
      if (quizLocked) {
        setError("Debes terminar todos los slides para desbloquear el quiz.");
        return;
      }

      const res = await fetch(`${API_URL}/api/auth/roadmap/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        credentials: "include",
        body: JSON.stringify({ topic }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || "No se pudo completar");
        return;
      }

      setProgress(data.roadmap || {});
      setMsg("Marcado como completado");
    } finally {
      setSending(false);
    }
  };

  // Si cambias de topic, resetea slideIndex y respuestas
  useEffect(() => {
    setSlideIndex(0);
    setAnswers({});
    setError("");
    setMsg("");
  }, [topic]);

  useEffect(() => {
    if (!content) return;

    const total = slides.length || 0;
    const current = total ? slideIndex + 1 : 0;
    const slideTitle = total ? slides[slideIndex]?.title : "Contenido en preparación";

    setHeroTopic({
      title: content.title,
      subtitle:
        "Lee los slides y luego completa el quiz. Antes de contestar debes leerlos todos.",
      metaLeftTitle: "Slides",
      metaLeftValue: total ? `${current}/${total}` : "0/0",
      metaMidTitle: "Slide actual",
      metaMidValue: slideTitle,
      metaMidDesc: total
        ? "Usa Anterior/Siguiente o los puntos para navegar."
        : "Aún no hay slides para este tema.",
      metaRightTitle: "Quiz",
      metaRightValue: quiz.length ? `${quiz.length} preguntas` : "Pendiente",
      metaRightDesc: done
        ? "Tema completado"
        : quizLocked
        ? "Bloqueado hasta terminar los slides"
        : "Completa el quiz para marcar progreso.",
    });

    return () => setHeroTopic(null);
  }, [content, slides, slideIndex, quiz.length, done, quizLocked, setHeroTopic]);

  if (!content) {
    return (
      <div>
        <button
          className="dash-btn ghost"
          onClick={() => navigate("/roadmap")}
          style={{ marginBottom: 12 }}
        >
          ← Volver al roadmap
        </button>
        <p className="error">Tema no válido.</p>
      </div>
    );
  }

  return (
    <div>
      {/* ===== Swiper / Slides ===== */}
      <div className="rm-swiper">
        {slides.length === 0 ? (
          <div className="rm-slide">
            <h2>Contenido en preparación</h2>
            <p className="muted">Aún no hay slides para este tema.</p>
          </div>
        ) : (
          <div className="rm-slide">
            <div className="rm-slide-head">
              <h2 style={{ margin: 0 }}>{slides[slideIndex].title}</h2>
              <div className="muted">
                Slide {slideIndex + 1}/{slides.length}
              </div>
            </div>

            <div className="rm-slide-body">
              {slides[slideIndex].paragraphs.map((p, idx) => (
                <p key={idx} className="rm-p">
                  {p}
                </p>
              ))}
            </div>

            <div className="rm-slide-controls">
                <button
                    type="button"
                    className="dash-btn ghost rm-nav-btn"
                    disabled={!canGoPrev}
                    aria-disabled={!canGoPrev}
                    aria-label="Slide anterior"
                    onClick={() => {
                    if (!canGoPrev) return;           // ✅ blindaje
                    setSlideIndex((i) => i - 1);
                    }}
                >
                    <span aria-hidden="true">←</span>
                    <span className="rm-nav-text">Anterior</span>
                </button>

                <div className="rm-dots">
                    {slides.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        className={`rm-dot ${i === slideIndex ? "active" : ""}`}
                        onClick={() => setSlideIndex(i)}
                        aria-label={`Ir al slide ${i + 1}`}
                    />
                    ))}
                </div>

                <button
                    type="button"
                    className="dash-btn rm-nav-btn"
                    disabled={!canGoNext}
                    aria-disabled={!canGoNext}
                    aria-label="Slide siguiente"
                    onClick={() => {
                    if (!canGoNext) return;           // ✅ blindaje
                    setSlideIndex((i) => i + 1);
                    }}
                >
                    <span className="rm-nav-text">Siguiente</span>
                    <span aria-hidden="true">→</span>
                </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== Quiz ===== */}
      <div style={{ marginTop: 18 }}>
        <h2>Quiz</h2>

        {/* Si está bloqueado, NO renderizamos el quiz interactivo */}
        {quizLocked ? (
          <div
            style={{
              borderRadius: 12,
              padding: 16,
              border: "1px solid rgba(0,0,0,.12)",
              background: "rgba(255,255,255,.45)",
              fontWeight: 700,
            }}
          >
            Debes terminar la lección (llegar al último slide) para poder acceder al quiz.
          </div>
        ) : quiz.length === 0 ? (
          <p className="muted-quiz">Quiz pendiente para este tema.</p>
        ) : (
          <div className="quiz">
            {quiz.map((item, i) => (
              <div key={i} className="quiz-q">
                <div className="quiz-title">
                  {i + 1}. {item.q}
                </div>
                <div className="quiz-options">
                  {item.options.map((opt, idx) => (
                    <label key={idx} className="quiz-opt">
                      <input
                        type="radio"
                        name={`q-${i}`}
                        checked={answers[i] === idx}
                        onChange={() => setAnswers((a) => ({ ...a, [i]: idx }))}
                        disabled={done}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!!quiz.length && !done && !quizLocked && (
          <div className="muted-quiz" style={{ marginTop: 8 }}>
            Puntuación estimada (según tus respuestas): <strong>{scorePreview}%</strong>
          </div>
        )}

        <div className="botones" style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <button
            className="dash-btn"
            onClick={submitQuiz}
            disabled={sending || done || !quiz.length || quizLocked}
          >
            {done ? "Completado" : sending ? "Enviando..." : "Enviar quiz"}
          </button>

          {!done && (
            <button
              className="dash-btn ghost"
              onClick={completeManually}
              disabled={sending || quizLocked}
            >
              Marcar como completado
            </button>
          )}
        </div>

        {error && <p className="error">{error}</p>}
        {msg && <p className="success">{msg}</p>}
      </div>
    </div>
  );
}
